import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbFilterPlus } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { customFilterService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { CustomFilter } from '@/types';

import { CustomFilterActions, CustomFilterState } from '../types';

export const createCustomFilterActions: StateCreator<
    CustomFilterState & CustomFilterActions,
    [['zustand/immer', never]],
    [],
    CustomFilterActions
> = (set, get, store) => ({
    // Core
    fetchCustomFilters: async (params) => {
        set({ loading: true });

        try {
            const customFilters = await customFilterService.list(params);

            set({ customFilters, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load customFilters' });
        }
    },
    fetchCustomFilterById: async (id) => {
        set((state) => {
            state.loadingCustomFilter = true;
            state.error = undefined;
        });

        try {
            const customFilter = await customFilterService.getById(id);

            set({ selectedCustomFilter: customFilter, loadingCustomFilter: false });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch customFilter detail';
                state.loadingCustomFilter = false;
            });
        }
    },
    clearSelectedCustomFilter: () => {
        set((state) => {
            state.selectedCustomFilter = null;
        });
    },
    createCustomFilter: async (input) =>
        runSingleFlight(createSingleFlightKey('customFilters:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as CustomFilter;

            set({ loadingCustomFilter: true, customFilters: appendEntity(get().customFilters, optimistic) });

            try {
                const customFilter = await customFilterService.create(input);

                set({
                    customFilters: replaceOptimisticEntityById(get().customFilters, tempId, customFilter),
                    loadingCustomFilter: false,
                });

                toast(`Custom Filter Created`, {
                    icon: createElement(TbFilterPlus, { size: '1rem' }),
                    description: customFilter.title,
                });

                return customFilter;
            } catch (error) {
                captureException(error);

                set({
                    customFilters: removeEntityById(get().customFilters, tempId),
                    error: 'Failed to create customFilter',
                    loadingCustomFilter: false,
                });
            }
        }),
    updateCustomFilter: async (id, updates) => {
        const prev = get().customFilters.find((f) => f.id === id);
        if (!prev) {
            return;
        }

        set({
            customFilters: mergeEntityById(get().customFilters, id, updates),
            selectedCustomFilter:
                get().selectedCustomFilter?.id === id
                    ? mergeEntity(get().selectedCustomFilter!, updates)
                    : get().selectedCustomFilter,
        });

        try {
            const customFilter = await customFilterService.update(id, updates);

            set({
                customFilters: mergeEntityById(get().customFilters, id, customFilter),
                selectedCustomFilter:
                    get().selectedCustomFilter?.id === id
                        ? mergeEntity(get().selectedCustomFilter!, customFilter)
                        : get().selectedCustomFilter,
            });
        } catch (error) {
            captureException(error);

            set({
                customFilters: replaceEntityById(get().customFilters, id, prev),
                selectedCustomFilter: get().selectedCustomFilter?.id === id ? prev : get().selectedCustomFilter,
                error: 'Failed to update customFilter',
            });
        }
    },
    deleteCustomFilter: async (id) => {
        const prev = get().customFilters;

        set({
            customFilters: removeEntityById(get().customFilters, id),
            selectedCustomFilter: get().selectedCustomFilter?.id === id ? null : get().selectedCustomFilter,
        });

        try {
            await customFilterService.delete(id);
        } catch (error) {
            captureException(error);

            set({ customFilters: prev, error: 'Failed to delete customFilter' });
        }
    },
    // - Aliases
    duplicateCustomFilter: async (id) => {
        const prev = get().customFilters;

        if (!prev) {
            return;
        }

        try {
            const duplicated = await customFilterService.duplicate(id);

            set({ customFilters: upsertEntityById(get().customFilters, duplicated, { prepend: true }) });

            toast(`CustomFilter Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.title,
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ customFilters: prev, error: 'Failed to duplicate customFilter' });

            toast.error('Failed to duplicate customFilter');
        }
    },
});
